import React, { useEffect, useRef, useState } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceArea,
  ReferenceLine,
} from 'recharts';

export default function SizePlot({
  data,
  lines = [],
  title,
  referenceAreas = [],
  referenceLines = [],
  selectionEnabled = false,
  selectedWindow = null,
  selectionKey = null,
  onSelectWindow,
}) {
  const [drag, setDrag] = useState(null);
  const dragRef = useRef(null);
  const cancelDrag = () => { dragRef.current = null; setDrag(null); };
  useEffect(() => {
    dragRef.current = null;
    setDrag(null);
  }, [data, selectionEnabled, selectionKey, selectedWindow?.start, selectedWindow?.end]);
  const snap = (event) => {
    const raw = event?.activeLabel ?? event?.activePayload?.[0]?.payload?.time;
    if (raw == null || raw === '' || !Number.isFinite(Number(raw))) return null;
    const times = (data || []).map((point) => point.time).filter((time) => typeof time === 'number' && Number.isFinite(time));
    return times.length ? times.reduce((best, time) => Math.abs(time - Number(raw)) < Math.abs(best - Number(raw)) ? time : best) : null;
  };
  const startDrag = (event) => {
    if (!selectionEnabled) return;
    const start = snap(event);
    if (start == null) return;
    dragRef.current = { start, end: start };
    setDrag(dragRef.current);
  };
  const moveDrag = (event) => {
    if (!selectionEnabled || !dragRef.current) return;
    const end = snap(event);
    if (end == null) return;
    dragRef.current = { ...dragRef.current, end };
    setDrag(dragRef.current);
  };
  const finishDrag = (event) => {
    const current = dragRef.current;
    const end = snap(event) ?? current?.end;
    cancelDrag();
    if (!selectionEnabled || !current || end == null || current.start === end) return;
    onSelectWindow?.({ start: Math.min(current.start, end), end: Math.max(current.start, end) });
  };
  const interval = drag || selectedWindow;
  const selectionArea = interval && Number.isFinite(interval.start) && Number.isFinite(interval.end) && interval.start !== interval.end
    ? [{ key: 'manual-window', x1: Math.min(interval.start, interval.end), x2: Math.max(interval.start, interval.end),
      fill: '#0284c7', fillOpacity: 0.15, label: { value: drag ? 'Selecting fitting window' : 'Manual fitting window', position: 'insideTop', fontSize: 12 } }]
    : [];
  if (!data || data.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2">
      {title && <h3 className="text-lg font-semibold">{title}</h3>}
      <div style={{ width: '100%', height: 320, cursor: selectionEnabled ? 'crosshair' : undefined, userSelect: selectionEnabled ? 'none' : undefined }}>
        <ResponsiveContainer>
          <LineChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
            onMouseDown={selectionEnabled ? startDrag : undefined}
            onMouseMove={selectionEnabled ? moveDrag : undefined}
            onMouseUp={selectionEnabled ? finishDrag : undefined}
            onMouseLeave={selectionEnabled ? cancelDrag : undefined}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="time" type="number" domain={[data[0].time, data[data.length - 1].time]} tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip formatter={(value) => (typeof value === 'number' ? value.toFixed(3) : value)} />
            <Legend />
            {[...referenceAreas, ...selectionArea].map((area) => (
              <ReferenceArea
                key={area.key || `${area.x1}-${area.x2}`}
                x1={area.x1}
                x2={area.x2}
                fill={area.fill || '#2563eb'}
                fillOpacity={area.fillOpacity ?? 0.08}
                stroke={area.stroke}
                strokeOpacity={area.strokeOpacity ?? 0.2}
                label={area.label}
              />
            ))}
            {referenceLines.map((line) => (
              <ReferenceLine
                key={line.key || `${line.x}-${line.label || 'marker'}`}
                x={line.x}
                stroke={line.stroke || '#0f172a'}
                strokeDasharray={line.strokeDasharray || '4 3'}
                strokeOpacity={line.strokeOpacity ?? 0.75}
                label={line.label}
              />
            ))}
            {lines.map(line => (
              <Line
                key={line.dataKey}
                type="monotone"
                dataKey={line.dataKey}
                name={line.name}
                stroke={line.color}
                dot={false}
                strokeWidth={line.strokeWidth ?? 2}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
